import React, { useState, useEffect } from 'react';
import { doc, getDoc, addDoc, collection, query, where, onSnapshot, updateDoc, setDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import VendorAutocomplete from '../../components/common/VendorAutocomplete';
import { PaperclipIcon, TrashIcon } from '../../components/icons/Icons';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
    sendNotificationEmail,
    newVendorTemplate
} from '../../firebase/notifications';

// This component is the dedicated form for creating and editing purchase requests.
export default function NewPurchaseRequest({ userProfile, showModal, appId, onCancel, initialData = null, readOnly = false }) {
    const isEditMode = initialData !== null;

    // Form State
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [businessLine, setBusinessLine] = useState('');
    const [department, setDepartment] = useState('');
    const [description, setDescription] = useState('');
    const [vendorType, setVendorType] = useState('existing');
    const [selectedVendor, setSelectedVendor] = useState('');
    const [newVendorName, setNewVendorName] = useState('');
    const [newVendorContact, setNewVendorContact] = useState('');
    const [newVendorEmail, setNewVendorEmail] = useState('');
    const [paymentProcess, setPaymentProcess] = useState('oneTime');
    const [contractDuration, setContractDuration] = useState('');
    const [paymentFrequency, setPaymentFrequency] = useState('');
    const [engagementStartDate, setEngagementStartDate] = useState('');
    const [engagementEndDate, setEngagementEndDate] = useState('');
    const [currency, setCurrency] = useState('INR');
    const [approxValue, setApproxValue] = useState('');
    const [billingEntity, setBillingEntity] = useState('');
    const [files, setFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [formError, setFormError] = useState('');
    const [approvers, setApprovers] = useState([]);
    const [assignedApproverId, setAssignedApproverId] = useState('');

    useEffect(() => {
        if (!db || !userProfile) return;
        
        const usersCollectionPath = `/artifacts/${appId}/users`;
        let approversQuery;

        if (userProfile.role === 'Approver') {
            approversQuery = query(collection(db, usersCollectionPath), where("role", "==", "Admin"));
        } else {
            approversQuery = query(collection(db, usersCollectionPath), where("role", "in", ["Admin", "Approver"]));
        }

        const unsubscribe = onSnapshot(approversQuery, (snapshot) => {
            const approversData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setApprovers(approversData.filter(approver => approver.uid !== userProfile.uid));
        });
        
        return () => unsubscribe();
    }, [db, appId, userProfile]);

    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = date.toDate ? date.toDate() : new Date(date);
        return d.toISOString().split('T')[0];
    };

    useEffect(() => {
        if (isEditMode) {
            setBusinessLine(initialData.businessLine || '');
            setDepartment(initialData.department || '');
            setDescription(initialData.description || '');
            setVendorType(initialData.vendorType || 'existing');
            setSelectedVendor(initialData.selectedVendor || '');
            setNewVendorName(initialData.newVendorName || '');
            setNewVendorContact(initialData.newVendorContact || '');
            setNewVendorEmail(initialData.newVendorEmail || '');
            setPaymentProcess(initialData.paymentProcess || 'oneTime');
            setContractDuration(initialData.contractDuration || '');
            setPaymentFrequency(initialData.paymentFrequency || '');
            setEngagementStartDate(formatDateForInput(initialData.engagementStartDate));
            setEngagementEndDate(formatDateForInput(initialData.engagementEndDate));
            setCurrency(initialData.currency || 'INR');
            setApproxValue(initialData.approxValue || '');
            setBillingEntity(initialData.billingEntity || '');
            setExistingAttachments(initialData.fileAttachments || []);
            setAssignedApproverId(initialData.assignedApproverId || '');
        } else {
            setDepartment(userProfile?.department || '');
        }
    }, [initialData, isEditMode, userProfile]);

    const departments = ['Business Development', 'Learning Design', 'HR', 'Admin', 'Finance', 'IT', 'Marketing', 'Teaching Operations', 'Channel Sales', 'Applications & Student Support'];
    const currencies = ['USD', 'SGD', 'INR', 'EUR', 'GBP', 'AUD'];

    const handleFileChange = (e) => {
        setFiles(prevFiles => [...prevFiles, ...Array.from(e.target.files)]);
    };

    const removeFile = (indexToRemove) => {
        setFiles(files.filter((_, index) => index !== indexToRemove));
    };

    const removeExistingAttachment = (fileToRemove) => {
        setExistingAttachments(existingAttachments.filter(file => file.url !== fileToRemove.url));
    };

    const generateRequestNumber = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const randomSuffix = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
        return `PR-${year}${month}${day}-${randomSuffix}`;
    };

    const handleCreateRequest = async (requestData) => {
        if (!db || !userProfile.uid || !storage) {
            showModal("Error", "Database or storage connection not available.");
            return;
        }
        try {
            const uploadedFileObjects = await Promise.all(
                requestData.files.map(async (file) => {
                    const filePath = `attachments/requests/${userProfile.uid}/${Date.now()}-${file.name}`;
                    const fileRef = ref(storage, filePath);
                    await uploadBytes(fileRef, file);
                    const downloadURL = await getDownloadURL(fileRef);
                    return { name: file.name, url: downloadURL, path: filePath };
                })
            );

            const { files, ...dataToSave } = requestData;
            const requestsCollectionPath = `/artifacts/${appId}/public/data/purchaseRequests`;
            
            const newRequestRef = doc(collection(db, requestsCollectionPath));

            await setDoc(newRequestRef, {
                ...dataToSave,
                id: newRequestRef.id,
                fileAttachments: uploadedFileObjects,
                requesterId: userProfile.uid,
                requesterName: userProfile?.name || 'Unknown',
                status: 'Pending Manager Approval',
                createdAt: serverTimestamp(),
            });

            if (dataToSave.vendorType === 'new') {
                const vendorsCollectionPath = `/artifacts/${appId}/public/data/vendors`;
                await addDoc(collection(db, vendorsCollectionPath), {
                    companyName: dataToSave.newVendorName,
                    contactPerson: dataToSave.newVendorContact,
                    email: dataToSave.newVendorEmail,
                    status: 'Pending Approval',
                    purchaseRequestId: newRequestRef.id, 
                    pr_approved: false,
                    createdAt: serverTimestamp(),
                    submittedBy: userProfile?.name
                });
            }

            onCancel();
        } catch (e) {
            console.error("Error adding document or files: ", e);
            showModal("Error", "Failed to create new request.");
        }
    };

    const handleUpdateRequest = async (requestId, dataFromForm) => {
        if (!db || !userProfile.uid || !storage) {
            showModal("Error", "Database or storage connection not available.");
            return;
        }
        try {
            const requestDocRef = doc(db, `/artifacts/${appId}/public/data/purchaseRequests`, requestId);
            const originalDocSnap = await getDoc(requestDocRef);
            const originalAttachments = originalDocSnap.data()?.fileAttachments || [];

            const newUploadedFiles = await Promise.all(
                dataFromForm.files.map(async (file) => {
                    const filePath = `attachments/requests/${userProfile.uid}/${Date.now()}-${file.name}`;
                    const fileRef = ref(storage, filePath);
                    await uploadBytes(fileRef, file);
                    const downloadURL = await getDownloadURL(fileRef);
                    return { name: file.name, url: downloadURL, path: filePath };
                })
            );

            const remainingAttachments = dataFromForm.fileAttachments;
            const deletedAttachments = originalAttachments.filter(originalFile =>
                !remainingAttachments.some(remainingFile => (remainingFile.url === originalFile.url))
            );

            await Promise.all(
                deletedAttachments.map(fileToDelete => {
                    if (fileToDelete.path) {
                        const fileRef = ref(storage, fileToDelete.path);
                        return deleteObject(fileRef).catch(err => console.error("Failed to delete file:", err));
                    }
                    return Promise.resolve();
                })
            );

            const finalAttachments = [...remainingAttachments, ...newUploadedFiles];
            const { files, ...restOfData } = dataFromForm;
            const updatePayload = {
                ...restOfData,
                fileAttachments: finalAttachments,
                status: 'Pending Manager Approval',
                updatedAt: serverTimestamp(),
            };

            await updateDoc(requestDocRef, updatePayload);

            if (dataFromForm.vendorType === 'new') {
                const vendorsQuery = query(
                    collection(db, `/artifacts/${appId}/public/data/vendors`),
                    where("purchaseRequestId", "==", requestId)
                );
                const vendorSnap = await getDocs(vendorsQuery);
                if (!vendorSnap.empty) {
                    const vendorDoc = vendorSnap.docs[0];
                    await updateDoc(vendorDoc.ref, { 
                        status: 'Pending Approval',
                        companyName: dataFromForm.newVendorName,
                        contactPerson: dataFromForm.newVendorContact,
                        email: dataFromForm.newVendorEmail
                    });
                }
            }

            onCancel();
        } catch (e) {
            console.error("Error updating document or files: ", e);
            showModal("Error", "Failed to update request.");
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError('');

        if (!businessLine || !department || !assignedApproverId || !description.trim() || !engagementStartDate || !engagementEndDate || !paymentProcess || !approxValue || !currency || !billingEntity) {
            return setFormError('Please fill all required fields.');
        }
        if (new Date(engagementEndDate) < new Date(engagementStartDate)) {
            return setFormError('Engagement End Date cannot be before the Start Date.');
        }
        if (vendorType === 'existing' && !selectedVendor) {
            return setFormError('Please select an existing vendor.');
        }
        if (vendorType === 'new' && (!newVendorName.trim() || !newVendorContact.trim() || !newVendorEmail.trim())) {
            return setFormError('Please enter all details for the new vendor.');
        }

        const requestData = {
            businessLine, department, description, vendorType, selectedVendor, newVendorName,
            newVendorContact, newVendorEmail, paymentProcess, contractDuration, paymentFrequency,
            engagementStartDate: new Date(engagementStartDate),
            engagementEndDate: new Date(engagementEndDate),
            currency, approxValue: parseFloat(approxValue), billingEntity,
            files: files,
            fileAttachments: existingAttachments,
            assignedApproverId,
            requestNumber: isEditMode ? initialData.requestNumber : generateRequestNumber(),
        };

        setIsSubmitting(true);

        // --- FIX: Use setTimeout to allow the UI to re-render BEFORE the heavy work begins ---
        setTimeout(async () => {
            const requestData = {
                businessLine, department, description, vendorType, selectedVendor, newVendorName,
                newVendorContact, newVendorEmail, paymentProcess, contractDuration, paymentFrequency,
                engagementStartDate: new Date(engagementStartDate),
                engagementEndDate: new Date(engagementEndDate),
                currency, approxValue: parseFloat(approxValue), billingEntity,
                files: files,
                fileAttachments: existingAttachments,
                assignedApproverId,
                requestNumber: isEditMode ? initialData.requestNumber : generateRequestNumber(),
            };
            
            try {
                if (isEditMode) {
                    await handleUpdateRequest(initialData.id, requestData);
                } else {
                    await handleCreateRequest(requestData);
                }
            } catch (error) {
                console.error("Submission failed at handleSubmit level:", error);
            } finally {
                // This will now run after the await is complete and the UI has updated
                setIsSubmitting(false);
            }
        }, 0); // A delay of 0ms is enough to push this to the next event loop cycle
    };

    const valueLabel = paymentProcess === 'oneTime' ? 'Approx. Value Per Invoice' : 'Total Contract Value';
    const formTitle = isEditMode ? `Viewing Request: ${initialData.requestNumber}` : 'Create New Purchase Request';

    return (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-[#213368] mb-6">{formTitle}</h2>
            
            {isEditMode && !readOnly && (
                <div className="mb-4 p-3 rounded-md text-sm bg-green-100 text-green-800">
                    <p>This request was rejected. You can edit the details and resubmit.</p>
                    {initialData.status === 'Rejected by Finance' && initialData.financeRejectionReason && (
                        <p className="mt-1 italic"><strong>Finance Comments:</strong> {initialData.financeRejectionReason}</p>
                    )}
                    {initialData.status === 'Rejected' && initialData.approvalComments && (
                        <p className="mt-1 italic"><strong>Approver's Comments:</strong> {initialData.approvalComments}</p>
                    )}
                </div>
            )}
            {isEditMode && readOnly && (
                 <p className="mb-4 p-3 rounded-md text-sm bg-yellow-100 text-yellow-800">
                    This request is currently {initialData.status} and cannot be edited.
                </p>
            )}

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md space-y-8 text-[#213368]">
                <fieldset disabled={readOnly} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium">Business Line</label>
                            <select value={businessLine} onChange={e => setBusinessLine(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6] disabled:bg-gray-100">
                                <option value="">Select a line...</option>
                                <option value="Teaching Services">Teaching Services</option>
                                <option value="TWG">TWG</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Department</label>
                            <select 
                                value={department} 
                                onChange={e => setDepartment(e.target.value)} 
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6] disabled:bg-gray-100"
                                disabled={userProfile.role !== 'Admin'}
                            >
                                {userProfile.role === 'Admin' ? (
                                    <>
                                        <option value="">Select a department...</option>
                                        {departments.map(d => <option key={d} value={d}>{d}</option>)}
                                    </>
                                ) : (
                                    <option value={department}>{department}</option>
                                )}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Approver</label>
                        <select value={assignedApproverId} onChange={e => setAssignedApproverId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6] disabled:bg-gray-100">
                            <option value="">Select an approver...</option>
                            {approvers.map(approver => (
                                <option key={approver.id} value={approver.id}>{approver.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Full Request Description</label>
                        <textarea value={description} onChange={e => setDescription(e.target.value)} rows="4" className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6] disabled:bg-gray-100" placeholder="Detailed description of the purchase..."></textarea>
                    </div>
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-4">Vendor Information</h3>
                        <div className="flex items-center space-x-6 mb-4">
                            <label className="flex items-center">
                                <input type="radio" name="vendorType" value="existing" checked={vendorType === 'existing'} onChange={() => setVendorType('existing')} className="form-radio h-4 w-4 text-[#20BEC6] focus:ring-[#20BEC6] disabled:opacity-50" />
                                <span className="ml-2 text-sm">Existing Vendor</span>
                            </label>
                            <label className="flex items-center">
                                <input type="radio" name="vendorType" value="new" checked={vendorType === 'new'} onChange={() => setVendorType('new')} className="form-radio h-4 w-4 text-[#20BEC6] focus:ring-[#20BEC6] disabled:opacity-50" />
                                <span className="ml-2 text-sm">Add New Vendor</span>
                            </label>
                        </div>
                        {vendorType === 'existing' ? (
                            <div>
                                <label className="block text-sm font-medium">Select Vendor</label>
                                <VendorAutocomplete
                                    appId={appId}
                                    onVendorSelect={setSelectedVendor}
                                    initialValue={selectedVendor}
                                />
                            </div>
                        ) : (
                            <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                                <div>
                                    <label className="block text-sm font-medium">New Vendor Name</label>
                                    <input type="text" value={newVendorName} onChange={e => setNewVendorName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100" placeholder="Enter new vendor's company name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium">Contact Person</label>
                                    <input type="text" value={newVendorContact} onChange={e => setNewVendorContact(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100" placeholder="e.g., Jane Doe" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium">Vendor Email</label>
                                    <input type="email" value={newVendorEmail} onChange={e => setNewVendorEmail(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100" placeholder="e.g., contact@vendor.com" />
                                </div>
                                <p className="text-xs text-gray-500 pt-2">Finance will need to approve this new vendor after submission.</p>
                            </div>
                        )}
                    </div>
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-4">Invoicing & Payment</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium">Engagement Start Date</label>
                                <input type="date" value={engagementStartDate} onChange={e => setEngagementStartDate(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Engagement End Date</label>
                                <input type="date" value={engagementEndDate} onChange={e => setEngagementEndDate(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100" />
                            </div>
                        </div>
                        <div className="mt-6">
                            <label className="block text-sm font-medium mb-2">Payment Process</label>
                            <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-2 sm:space-y-0">
                                <label className="flex items-center"><input type="radio" name="paymentProcess" value="oneTime" checked={paymentProcess === 'oneTime'} onChange={e => setPaymentProcess(e.target.value)} className="form-radio text-[#20BEC6] focus:ring-[#20BEC6]" /><span className="ml-2">Non-Contracted / One time</span></label>
                                <label className="flex items-center"><input type="radio" name="paymentProcess" value="contracted" checked={paymentProcess === 'contracted'} onChange={e => setPaymentProcess(e.target.value)} className="form-radio text-[#20BEC6] focus:ring-[#20BEC6]" /><span className="ml-2">Contracted</span></label>
                                <label className="flex items-center"><input type="radio" name="paymentProcess" value="saas" checked={paymentProcess === 'saas'} onChange={e => setPaymentProcess(e.target.value)} className="form-radio text-[#20BEC6] focus:ring-[#20BEC6]" /><span className="ml-2">SaaS</span></label>
                            </div>
                        </div>
                        {(paymentProcess === 'contracted' || paymentProcess === 'saas') && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                <div>
                                    <label className="block text-sm font-medium">Contract Duration (months)</label>
                                    <input type="number" value={contractDuration} onChange={e => setContractDuration(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100" placeholder="e.g., 12" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium">Frequency of Payment</label>
                                    <select value={paymentFrequency} onChange={e => setPaymentFrequency(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100">
                                        <option value="">Select frequency...</option>
                                        <option value="Monthly">Monthly</option>
                                        <option value="Quarterly">Quarterly</option>
                                        <option value="Annually">Annually</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div>
                                <label className="block text-sm font-medium">{valueLabel}</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 sm:text-sm">{currency}</span>
                                    </div>
                                    <input type="number" value={approxValue} onChange={e => setApproxValue(e.target.value)} className="block w-full p-2 pl-12 pr-12 border-gray-300 rounded-md disabled:bg-gray-100" placeholder="0.00" />
                                    <div className="absolute inset-y-0 right-0 flex items-center">
                                        <select value={currency} onChange={e => setCurrency(e.target.value)} className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-md focus:ring-[#20BEC6] disabled:bg-gray-100">
                                            {currencies.map(c => <option key={c}>{c}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Billing Entity</label>
                                <select value={billingEntity} onChange={e => setBillingEntity(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6] disabled:bg-gray-100">
                                    <option value="">Select entity...</option>
                                    <option value="Singapore">Singapore</option>
                                    <option value="India">India</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-4">Supporting Documents</h3>
                        {!readOnly && (
                            <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-blue rounded-lg shadow-md tracking-wide uppercase border border-blue cursor-pointer hover:bg-gray-100">
                                <svg className="w-8 h-8" fill="#20BEC6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                    <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4 4-4-4h3v-4h2v4z" />
                                </svg>
                                <span className="mt-2 text-base leading-normal">Select files to add</span>
                                <input type='file' className="hidden" multiple onChange={handleFileChange} />
                            </label>
                        )}
                        {(existingAttachments.length > 0 || files.length > 0) && (
                            <div className="mt-4">
                                <p className="text-sm font-medium">Attached files:</p>
                                <ul className="list-disc list-inside mt-2 text-sm text-gray-600">
                                    {existingAttachments.map((file, index) => (
                                        <li key={`exist-${index}`} className="flex items-center justify-between p-1">
                                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                                                <PaperclipIcon /> {file.name}
                                            </a>
                                            {!readOnly && (
                                                <button type="button" onClick={() => removeExistingAttachment(file)}>
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                    {files.map((file, index) => (
                                        <li key={`new-${index}`} className="flex items-center justify-between p-1">
                                            <span className="flex items-center"><PaperclipIcon /> {file.name}</span>
                                            {!readOnly && (
                                                <button type="button" onClick={() => removeFile(index)}>
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </fieldset>
                
                {formError && <p className="text-sm text-red-600 text-center pt-4">{formError}</p>}
                <div className="flex justify-end space-x-4 pt-5 border-t">
                    <button type="button" onClick={onCancel} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">
                        {isEditMode ? 'Close' : 'Cancel'}
                    </button>
                    {!readOnly && (
                        <button 
                            type="submit" 
                            disabled={isSubmitting}
                            className="flex justify-center items-center px-6 py-2 w-48 bg-[#20BEC6] text-white font-medium rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <LoadingSpinner size="sm" /> // Render the spinner
                            ) : (
                                // Render the text
                                isEditMode ? 'Update & Resubmit' : 'Submit Request'
                            )}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}